import React from 'react';
import dayjs from 'dayjs';

import { Divider, Grid, Header } from '@mairistem/composants';

import { PanelButtonEdition } from '@mairistem/administres-utils';

import type { Pacs, Registre } from '@/types';

import InformationsPacsModal from './InformationsPacsModal';

interface InformationPanelProps {
  pacs?: Partial<Pacs>;
  registre?: Partial<Registre> | null;
  updated?: boolean;
  onChange?: (data: Partial<Pacs>) => void;
  onSave?: () => Promise<void>;
  onRollback?: () => void;
}

const InformationPacsPanel = (props: InformationPanelProps) => {
  const { pacs, registre, updated, onChange, onSave, onRollback } = props;

  const anneeRegistre = pacs?.anneeRegistre;
  const typeRegistre = pacs?.typeRegistre;
  const numero = pacs?.numero;
  const dateInitialeInsee = pacs?.dateInitialeInsee;
  const dateDissolutionInsee = pacs?.dateDissolutionInsee;
  const statut = pacs?.derniereConvention?.statut;
  const dateIrrecevabilite = pacs?.derniereConvention?.dateIrrecevabilite;
  const irrecevabilite = (pacs?.derniereConvention?.irrecevabilite as any)
    ?.libelle;

  const [modalEditionOpen, setModalEditionOpen] =
    React.useState<boolean>(false);

  const handleClose = React.useCallback(() => {
    setModalEditionOpen(false);
  }, []);

  const handleCancel = React.useCallback(() => {
    onRollback?.();
    handleClose();
  }, [handleClose, onRollback]);

  const [statutColor, setStatutColor] = React.useState<string>('black');

  React.useEffect(() => {
    switch (statut) {
      case 'En cours':
        setStatutColor('#1faf89');
        break;
      case 'Irrecevable':
        setStatutColor('#971c82');
        break;
      case 'Dissous':
        setStatutColor('#c01622');
        break;
      default:
        setStatutColor('black');
    }
  }, [statut]);

  return (
    <Grid>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            INFORMATIONS DU PACS
          </Header>
          <PanelButtonEdition
            onClick={() => setModalEditionOpen(true)}
            data-testid="panel-edit-modal-informations-pacs"
          />
        </div>
        {modalEditionOpen && (
          <InformationsPacsModal
            numero={numero ?? ''}
            dateEnregistrement={
              pacs?.derniereConvention?.dateEnregistrement ?? ''
            }
            statut={pacs?.derniereConvention?.statut}
            enregistre={!!pacs?.derniereConvention?.editionRegistre}
            dateIrrecevabilite={
              pacs?.derniereConvention?.dateIrrecevabilite ?? ''
            }
            identifiantIrrecevabilite={
              pacs?.derniereConvention?.identifiantIrrecevabilite ?? 0
            }
            anneeRegistre={pacs?.anneeRegistre ?? 0}
            libelleRegistre={pacs?.libelleRegistre ?? ''}
            ordre={pacs?.derniereConvention?.ordre}
            dateInitialeInsee={dateInitialeInsee ?? ''}
            dateDissolutionInsee={dateDissolutionInsee ?? ''}
            registre={registre}
            updated={updated ?? false}
            onChange={onChange ?? (() => {})}
            onSave={onSave ?? (() => Promise.resolve())}
            onClose={handleClose}
            onCancel={handleCancel}
          />
        )}
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          <span>
            <b>Enregistré sur le registre : </b>
          </span>
          <span>
            Année {anneeRegistre} - {typeRegistre}
          </span>
        </Grid.Column>
        <Grid.Column width={6}>
          <span>
            <b>Numéro : </b>
          </span>
          <span>{numero}</span>
        </Grid.Column>
        <Grid.Column width={5}>
          <span>
            <b>Statut : </b>
          </span>
          <span style={{ color: statutColor }}>{statut}</span>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          <span>
            <b>Irrecevabilité en date du : </b>
          </span>
          <span>
            {dateIrrecevabilite
              ? dayjs(dateIrrecevabilite).format('DD/MM/YYYY')
              : 'Non concerné'}
          </span>
        </Grid.Column>
        <Grid.Column width={7}>
          <span>
            <b>Motif irrecevabilité : </b>
          </span>
          <span>{irrecevabilite ?? 'Non concerné'}</span>
        </Grid.Column>
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <Header as="h5" color="blue" style={{ margin: 0 }}>
          TRANSFERT À L&apos;INSEE
        </Header>
      </Grid.Row>
      <Grid.Row
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          flexWrap: 'nowrap',
        }}
      >
        <Grid.Column width={5}>
          <span>
            <b>Déclaration transférée à l&apos;INSEE le : </b>
          </span>
          <span>
            {dateInitialeInsee
              ? dayjs(dateInitialeInsee).format('DD/MM/YYYY')
              : 'Non transférée'}
          </span>
        </Grid.Column>
        <Grid.Column width={5}>
          <span>
            <b>Dissolution transférée à l&apos;INSEE le : </b>
          </span>
          <span>
            {dateDissolutionInsee
              ? dayjs(dateDissolutionInsee).format('DD/MM/YYYY')
              : 'Non transférée'}
          </span>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default InformationPacsPanel;
